/**
 * Wire shape for `BACKLOG.md` N3 — §170.315(b)(10) EHI export
 * (`patientAuth/chartRoutes.ts`'s `GET /patient-portal/chart/export`) — the single computable bundle
 * a patient can pull of their own record. Composed entirely from response schemas each record type
 * already validates its own live read against (`clinicalRequests.ts`, `labs.ts`,
 * `encounterRequests.ts`), the same "one shape, validated everywhere it crosses the wire" discipline
 * `authRequests.ts` states for its own schemas, rather than a parallel, driftable set of export-only
 * field lists.
 *
 * `formatVersion` is a literal, not a free-form string: a future pass changing this bundle's shape
 * bumps it, and any consumer (including this repo's own tests) can assert against a known value
 * rather than guessing at what changed.
 */

import { z } from 'zod';

import {
  allergyResponseSchema,
  immunizationResponseSchema,
  medicationResponseSchema,
  patientResponseSchema,
  problemResponseSchema,
  vitalResponseSchema,
} from './clinicalRequests.ts';
import { encounterResponseSchema, noteResponseSchema } from './encounterRequests.ts';
import { labResultResponseSchema } from './labs.ts';

export const EHI_EXPORT_FORMAT_VERSION = 'ehi-export-v1';

export const patientEhiExportSchema = z.strictObject({
  formatVersion: z.literal(EHI_EXPORT_FORMAT_VERSION),
  exportedAt: z.iso.datetime({ offset: true }),
  patient: patientResponseSchema,
  problems: z.array(problemResponseSchema),
  allergies: z.array(allergyResponseSchema),
  medications: z.array(medicationResponseSchema),
  immunizations: z.array(immunizationResponseSchema),
  vitals: z.array(vitalResponseSchema),
  labResults: z.array(labResultResponseSchema),
  encounters: z.array(encounterResponseSchema),
  notes: z.array(noteResponseSchema),
});
export type PatientEhiExport = z.infer<typeof patientEhiExportSchema>;

export const patientEhiExportResponseSchema = z.strictObject({
  export: patientEhiExportSchema,
});
export type PatientEhiExportResponse = z.infer<typeof patientEhiExportResponseSchema>;
